// src/components/Layout/Sidebar.js
import React from 'react';
import {
  Box,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Collapse,
  Toolbar,
} from '@mui/material';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';

import DashboardIcon from '@mui/icons-material/Dashboard';
import DriveEtaIcon from '@mui/icons-material/DriveEta';
import PeopleIcon from '@mui/icons-material/People';
import DescriptionIcon from '@mui/icons-material/Description';
import EventIcon from '@mui/icons-material/Event';
import SettingsIcon from '@mui/icons-material/Settings';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LogoutIcon from '@mui/icons-material/Logout';

import { useAuth } from '../../hooks/AuthContext';

// =========================
// NAV STRUCTURE (Grouped)
// =========================
const navSections = [
  {
    heading: 'Fleet',
    items: [
      {
        label: 'Vehicles',
        icon: <DriveEtaIcon />,
        children: [
          { label: 'View vehicles', to: '/vehicles' },
          { label: 'Add vehicle', to: '/vehicles/new' },
        ],
      },
      /*
      {
        label: 'Maintenance',
        icon: <BuildIcon />,
        children: [
          { label: 'Garage schedule', to: '/maintenance/schedule' },
          { label: 'Maintenance records', to: '/maintenance/records' },
        ],
      },
      */
      {
        label: 'Calendar',
        icon: <EventIcon />,
        to: '/calendar',
      },
    ],
  },
  {
    heading: 'Customers',
    items: [
      {
        label: 'Drivers',
        icon: <PeopleIcon />,
        children: [
          { label: 'View drivers', to: '/drivers' },
          { label: 'Add driver', to: '/drivers/new' },
        ],
      },
      {
        label: 'Hire Agreements',
        icon: <DescriptionIcon />,
        children: [
          { label: 'View hire agreements', to: '/hire-agreements' },
          { label: 'Add hire agreement', to: '/hire-agreements/new' },
        ],
      },
    ],
  },
  {
    heading: 'System',
    items: [
      {
        label: 'Settings',
        icon: <SettingsIcon />,
        to: '/settings',
      },
    ],
  },
];

export default function Sidebar({ collapsed = false }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();

  const [open, setOpen] = React.useState({});

  // Auto-open sections where a child route is active
  React.useEffect(() => {
    const updated = {};
    navSections.forEach((section) => {
      section.items.forEach((item) => {
        if (item.children) {
          const isActiveChild = item.children.some((child) =>
            location.pathname.startsWith(child.to)
          );
          updated[item.label] = isActiveChild;
        }
      });
    });
    setOpen((prev) => ({ ...prev, ...updated }));
  }, [location.pathname]);

  const toggleSection = (label) => {
    setOpen((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  return (
    <Box
      sx={{
        width: '100%',
        height: '100dvh',
        bgcolor: '#020B3A',
        color: 'white',
        display: 'flex',
        flexDirection: 'column',
        pb: 2,
      }}
    >
      <Toolbar sx={{mb: 2}} />

      <List>
        {/* Top-level Dashboard (outside any category) */}
        {(() => {
          const selected = location.pathname === '/';
          return (
            <ListItemButton
              key="dashboard"
              component={RouterLink}
              to="/"
              selected={selected}
              sx={{
                justifyContent: collapsed ? 'center' : 'flex-start',
                px: collapsed ? 0 : 2,
                mb: 1,
                '&.Mui-selected': {
                  bgcolor: 'rgba(255,255,255,0.12)',
                },
                '&.Mui-selected:hover': {
                  bgcolor: 'rgba(255,255,255,0.18)',
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: collapsed ? 0 : 2,
                  color: 'inherit',
                  justifyContent: 'center',
                }}
              >
                <DashboardIcon />
              </ListItemIcon>

              {!collapsed && (
                <ListItemText
                  primary="Dashboard"
                  primaryTypographyProps={{ fontSize: 14 }}
                />
              )}
            </ListItemButton>
          );
        })()}

        {/* Grouped sections */}
        {navSections.map((section) => (
          <Box key={section.heading} sx={{ mb: 1.5 }}>
            {/* SECTION HEADER */}
            {!collapsed && (
              <Box
                sx={{
                  px: 2,
                  py: 0.5,
                  fontSize: 11,
                  fontWeight: 600,
                  opacity: 0.6,
                  textTransform: 'uppercase',
                  letterSpacing: 0.8,
                }}
              >
                {section.heading}
              </Box>
            )}

            {/* SECTION LINKS */}
            {section.items.map((item) => {
              const hasChildren = !!item.children;
              const selectedParent =
                hasChildren &&
                item.children.some(
                  (child) => location.pathname === child.to
                );

              if (!hasChildren) {
                // Simple direct link
                const selected = location.pathname === item.to;
                return (
                  <ListItemButton
                    key={item.to}
                    component={RouterLink}
                    to={item.to}
                    selected={selected}
                    sx={{
                      justifyContent: collapsed ? 'center' : 'flex-start',
                      px: collapsed ? 0 : 2,
                      '&.Mui-selected': {
                        bgcolor: 'rgba(255,255,255,0.12)',
                      },
                      '&.Mui-selected:hover': {
                        bgcolor: 'rgba(255,255,255,0.18)',
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: collapsed ? 0 : 2,
                        color: 'inherit',
                        justifyContent: 'center',
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>

                    {!collapsed && (
                      <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{ fontSize: 14 }}
                      />
                    )}
                  </ListItemButton>
                );
              }

              // Parent with dropdown children (Vehicles, Drivers, etc.)
              const isOpen = !!open[item.label];

              const parentProps = collapsed
                ? { component: RouterLink, to: item.children[0].to }
                : { onClick: () => toggleSection(item.label) };

              return (
                <React.Fragment key={item.label}>
                  <ListItemButton
                    selected={selectedParent}
                    sx={{
                      justifyContent: collapsed ? 'center' : 'flex-start',
                      px: collapsed ? 0 : 2,
                      '&.Mui-selected': {
                        bgcolor: 'rgba(255,255,255,0.12)',
                      },
                      '&.Mui-selected:hover': {
                        bgcolor: 'rgba(255,255,255,0.18)',
                      },
                    }}
                    {...parentProps}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: collapsed ? 0 : 2,
                        color: 'inherit',
                        justifyContent: 'center',
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>

                    {!collapsed && (
                      <>
                        <ListItemText
                          primary={item.label}
                          primaryTypographyProps={{ fontSize: 14 }}
                        />
                        {isOpen ? (
                          <ExpandLessIcon fontSize="small" />
                        ) : (
                          <ExpandMoreIcon fontSize="small" />
                        )}
                      </>
                    )}
                  </ListItemButton>

                  {/* Children only visible when sidebar not collapsed */}
                  {!collapsed && (
                    <Collapse in={isOpen} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding>
                        {item.children.map((child) => {
                          const selected = location.pathname === child.to;
                          return (
                            <ListItemButton
                              key={child.to}
                              component={RouterLink}
                              to={child.to}
                              selected={selected}
                              sx={{
                                pl: 6,
                                '&.Mui-selected': {
                                  bgcolor: 'rgba(255,255,255,0.18)',
                                },
                                '&.Mui-selected:hover': {
                                  bgcolor: 'rgba(255,255,255,0.24)',
                                },
                              }}
                            >
                              <ListItemText
                                primary={child.label}
                                primaryTypographyProps={{ fontSize: 13 }}
                              />
                            </ListItemButton>
                          );
                        })}
                      </List>
                    </Collapse>
                  )}
                </React.Fragment>
              );
            })}
          </Box>
        ))}
      </List>

        {/* LOGOUT BUTTON */}
        <Box sx={{ mt: 'auto', px: collapsed ? 0 : 2 }}>
        <ListItemButton
            onClick={() => {
            logout();
            navigate('/login');
            }}
            sx={{
            justifyContent: collapsed ? 'center' : 'flex-start',
            px: collapsed ? 0 : 2,
            mt: 2,
            '&:hover': { bgcolor: 'rgba(255,255,255,0.18)' },
            }}
        >
            <ListItemIcon
            sx={{
                minWidth: 0,
                mr: collapsed ? 0 : 2,
                justifyContent: 'center',
                color: 'inherit',
            }}
            >
            <LogoutIcon />
            </ListItemIcon>

            {!collapsed && (
            <ListItemText
                primary="Logout"
                primaryTypographyProps={{ fontSize: 14 }}
            />
            )}
        </ListItemButton>
        </Box>
    </Box>
  );
}
